import {
  InboundReferenceCollection,
  InventoryTransactionType,
  OutboundReferenceCollection,
  ProductOutboundTransactionDoc,
} from '@gooduncles/gu-app-schema';
import { ProductInboundTransactionDoc } from '@gooduncles/gu-app-schema';
import { InventoryIssueDoc } from '@gooduncles/gu-app-schema';
import { useEffect, useState } from 'react';
import { combineLatest } from 'rxjs';
import {
  observeInventoryIssue,
  observeProductInboundTransaction,
  observeProductOutboundTransaction,
} from 'src/utils/firebase/firebase-short-cut';

export type InventoryIssueItem = InventoryIssueDoc & {
  transactionType: InventoryTransactionType | undefined;
  transaction: ProductInboundTransactionDoc | ProductOutboundTransactionDoc | undefined;
};

const useInventoryIssuesWithTransaction = (startDate: string, endDate: string) => {
  const [inventoryIssueItems, setInventoryIssueItems] = useState<InventoryIssueItem[] | null>(null);

  useEffect(() => {
    const inventoryIssue$ = observeInventoryIssue([], {
      sortKey: 'issueDate',
      orderBy: 'desc',
      startValue: startDate,
      endValue: endDate,
    });
    const inboundTransaction$ = observeProductInboundTransaction(
      [['referenceCollection', '==', InboundReferenceCollection.INVENTORY_ISSUE]],
      {
        sortKey: 'requestDate',
        orderBy: 'desc',
        startValue: startDate,
        endValue: endDate,
      }
    );
    const outboundTransaction$ = observeProductOutboundTransaction(
      [['referenceCollection', '==', OutboundReferenceCollection.INVENTORY_ISSUE]],
      {
        sortKey: 'requestDate',
        orderBy: 'desc',
        startValue: startDate,
        endValue: endDate,
      }
    );

    const subscription = combineLatest([inventoryIssue$, inboundTransaction$, outboundTransaction$]).subscribe(
      ([inventoryIssues, inboundTransactions, outboundTransactions]) => {
        setInventoryIssueItems(
          inventoryIssues
            .map((issue) => {
              const inboundTransaction = inboundTransactions.find(
                (transaction) => transaction.referenceId === issue._id
              );
              const outboundTransaction = outboundTransactions.find(
                (transaction) => transaction.referenceId === issue._id
              );
              return {
                ...issue,
                transactionType: inboundTransaction
                  ? InventoryTransactionType.INBOUND
                  : outboundTransaction
                  ? InventoryTransactionType.OUTBOUND
                  : undefined,
                transaction: inboundTransaction || outboundTransaction,
              };
            })
            .sort((a, b) => b._timeCreate.toDate().getTime() - a._timeCreate.toDate().getTime())
            .sort((a, b) => b.issueDate.localeCompare(a.issueDate))
        );
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [endDate, startDate]);

  return inventoryIssueItems;
};

export default useInventoryIssuesWithTransaction;
